/**
 * News Grid Component
 *
 * Grid container for news cards with Hero + Grid layout.
 * First featured item displayed as full-width hero,
 * remaining items in responsive grid.
 */

import { SimpleGrid, Button, Stack, Title } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { getRecentNews } from '../../../../data/news/newsData';
import { NewsCard } from './NewsCard';

interface NewsGridProps {
  maxItems?: number;
  showViewAll?: boolean;
  title?: string;
}

/**
 * News Grid Component
 *
 * Displays recent news items with Hero + Grid layout:
 * - First featured item: Full-width hero card
 * - Remaining items: Responsive grid (3 cols on desktop, 2 on tablet, 1 on mobile)
 */
export function NewsGrid({
  maxItems = 4,
  showViewAll = true,
  title = 'Aktualności',
}: NewsGridProps) {
  const newsItems = getRecentNews(maxItems);

  // Separate first featured item for hero display
  const heroItem = newsItems.find((item) => item.featured);
  const gridItems = heroItem
    ? newsItems.filter((item) => item.id !== heroItem.id)
    : newsItems;

  return (
    <Stack gap="xl">
      {/* Section Title */}
      <Title order={2} size="h2" ta="center">
        {title}
      </Title>

      {/* Hero Featured Card (full width) */}
      {heroItem && (
        <NewsCard
          item={heroItem}
          featured={true}
          showCTA={true}
          variant="hero"
        />
      )}

      {/* Regular News Grid */}
      {gridItems.length > 0 && (
        <SimpleGrid
          cols={{ base: 1, sm: 2, md: 3 }}
          spacing="lg"
        >
          {gridItems.map((item) => (
            <NewsCard
              key={item.id}
              item={item}
              featured={item.featured}
              showCTA={true}
              variant="default"
            />
          ))}
        </SimpleGrid>
      )}

      {/* View All Button */}
      {showViewAll && (
        <Button
          component={Link}
          to="/news"
          variant="outline"
          color="ecoGreen"
          size="md"
          rightSection={<IconArrowRight size={16} />}
          style={{ alignSelf: 'center' }}
        >
          Zobacz wszystkie aktualności
        </Button>
      )}
    </Stack>
  );
}
